<script setup lang="ts">
import { inject, ref } from "vue";
import { RouterLink } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import { useAgentDisplayMode } from "@/shared/agent-display/use-agent-display-mode";
import { DOCK_CLOSE } from "../composables/dock-close";
import type { Bubble } from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import TurnThumbs from "./TurnThumbs.vue";

/**
 * A BARRA SOB UMA FALA — a hora, e o que dá para fazer com ela.
 *
 * ── ÍCONES, NA ORDEM DA V2 (dono, 01/09, com o print na mão) ──────────────
 *
 * hora → copiar → usar como resposta → 👍 👎 → regenerar → apagar → ver o turno,
 * tudo à esquerda e do mesmo tamanho, como no app do Claude (dono, 25/10). Os verbos
 * eram palavras sublinhadas; viraram os mesmos traços de 12px da V2
 * (`BenPanel.vue`, `.ben-msg__action-btn`), com o rótulo no `title` e no
 * `aria-label` — uma barra de palavras sob cada resposta era a parte mais
 * barulhenta da fala, e é a menos lida.
 *
 * ── POR QUE ELA SOME (E QUANDO NÃO SOME) ──────────────────────────────────
 *
 * Ela existe SEMPRE no DOM e é a CSS que decide, exactamente como na V2. No
 * desktop está invisível até o rato passar por cima da fala; no toque, onde
 * `hover` não existe, está sempre visível — um controlo que só aparece no hover
 * não existe num telemóvel. E fica visível enquanto alguém tem o foco lá dentro
 * ou enquanto o painel do polegar está aberto, senão escrever o motivo de um 👎
 * far-se-ia com a caixa a desaparecer debaixo do cursor.
 *
 * ── O QUE SAIU DAQUI ──────────────────────────────────────────────────────
 *
 * Os tokens. Eles passaram para a linha da conta do cartão de atividade, ao lado
 * do modelo e do custo, que é onde uma conta se lê inteira.
 *
 * ── O APAGAR É DOIS CLIQUES, NUNCA UM `confirm()` ─────────────────────────
 *
 * O mesmo desenho da lista de sessões (`SessionList.vue`): o primeiro clique
 * arma (o traço fica vermelho e o rótulo pergunta), o segundo apaga, e sair com
 * o rato desarma. Um `confirm()` nativo é bloqueado em vista embutida; um modal
 * para uma fala é cerimónia demais. Só as falas GUARDADAS têm o lixinho — uma
 * fala ao vivo ainda não tem endereço (`bubble.id`) para apagar.
 */
const props = defineProps<{
	bubble: Bubble;
	/** Há um composer para onde mandar o texto (o Ben, ao lado da conversa). */
	canDraft?: boolean;
	canRegenerate?: boolean;
	/** A pergunta pode ser corrigida e reenviada. Só na fala da pessoa. */
	canEdit?: boolean;
	/** Há quem saiba apagar (a tela que tem a sessão). Sem dono, sem lixinho. */
	canRemove?: boolean;
}>();

const emit = defineEmits<{
	useAsDraft: [text: string];
	regenerate: [];
	edit: [];
	/** A fala guardada que a pessoa mandou apagar. Quem apaga é quem tem a sessão. */
	remove: [messageId: string];
	/** Repassado tal e qual: quem grava a opinião é quem tem a mutação — a fala. */
	rate: [payload: { rating: 1 | -1; note?: string }];
}>();

/** O mesmo traço de 22px para todos os verbos, com anel no foco do teclado. */
const ICON_BTN =
	"flex size-9 sm:size-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

const { t, clock } = useAgentsI18n();
const closeDock = inject(DOCK_CLOSE, null);
const { show } = useAgentDisplayMode();

/** Qual resposta acabou de ser copiada, para o traço confirmar por um instante. */
const copied = ref(false);
/** O painel do polegar está aberto: a barra não pode fugir enquanto se escreve. */
const pinned = ref(false);
/** O lixinho foi clicado uma vez e espera o segundo clique. */
const arming = ref(false);

async function copy(): Promise<void> {
	try {
		await navigator.clipboard.writeText(props.bubble.text);
		copied.value = true;
		setTimeout(() => {
			copied.value = false;
		}, 2000);
	} catch {
		// Bloqueado fora de um contexto seguro. O texto continua selecionável, então
		// uma falha aqui não precisa de mensagem própria.
	}
}

function removeStep(): void {
	if (!arming.value) {
		arming.value = true;
		return;
	}
	arming.value = false;
	if (props.bubble.id) emit("remove", props.bubble.id);
}
</script>

<template>
	<div
		v-if="show.actions"
		class="mt-0.5 flex flex-wrap items-center gap-0.5 text-3xs text-fg-subtle transition-opacity duration-150 focus-within:opacity-100 group-hover/bubble:opacity-100"
		:class="[
			/* Presa enquanto o painel do polegar está aberto — e então a regra do
			   hover não pode sequer existir, ou as duas disputam a mesma
			   especificidade e quem ganha passa a ser a ordem do CSS gerado. */
			pinned || arming ? 'opacity-100' : 'opacity-100 [@media(hover:hover)]:opacity-0',
			bubble.role === 'user' ? 'justify-end' : '',
		]"
	>
		<span v-if="show.timestamps && bubble.at" class="mr-1 tabular-nums">{{
			clock(bubble.at)
		}}</span>

		<!-- Corrigir a pergunta e mandar outra vez. Só na fala da pessoa. -->
		<button
			v-if="canEdit"
			type="button"
			:class="[ICON_BTN, 'hover:bg-bg-muted hover:text-fg']"
			:title="t('chat.editAndResend')"
			:aria-label="t('chat.editAndResend')"
			@click="emit('edit')"
		>
			<Icon name="pencil" class="size-5 sm:size-4" />
		</button>


		<button
			v-if="bubble.text"
			type="button"
			:class="[ICON_BTN, 'hover:bg-bg-muted hover:text-fg', copied ? 'text-success' : '']"
			:title="copied ? t('chat.copied') : t('chat.copy')"
			:aria-label="copied ? t('chat.copied') : t('chat.copy')"
			@click="copy"
		>
			<Icon :name="copied ? 'check' : 'copy'" class="size-5 sm:size-4" />
		</button>
		<!-- Um clique até o rascunho, e nada é enviado: quem envia é a pessoa. -->
		<button
			v-if="canDraft && bubble.text && !bubble.failed"
			type="button"
			:class="[ICON_BTN, 'hover:bg-bg-muted hover:text-fg']"
			:title="t('chat.useAsReply')"
			:aria-label="t('chat.useAsReply')"
			@click="emit('useAsDraft', bubble.text)"
		>
			<Icon name="reply" class="size-5 sm:size-4" />
		</button>
		<!-- O polegar entre copiar e regerar, na fila à esquerda, como no app do
		     Claude (dono, 25/10 — antes fechava a fila encostado à direita). -->
		<TurnThumbs
			v-if="bubble.role === 'assistant' && bubble.turnId && !bubble.failed"
			:turn-id="bubble.turnId"
			@open="pinned = $event"
			@rate="emit('rate', $event)"
		/>
		<button
			v-if="canRegenerate"
			type="button"
			:class="[ICON_BTN, 'hover:bg-bg-muted hover:text-fg']"
			:title="t('chat.regenerate')"
			:aria-label="t('chat.regenerate')"
			@click="emit('regenerate')"
		>
			<Icon name="refresh" class="size-5 sm:size-4" />
		</button>
		<button
			v-if="canRemove && bubble.id"
			type="button"
			:class="[ICON_BTN, arming ? 'bg-danger-subtle text-danger' : 'hover:bg-bg-muted hover:text-danger']"
			:title="arming ? t('chat.confirmDelete') : t('chat.deleteMessage')"
			:aria-label="arming ? t('chat.confirmDelete') : t('chat.deleteMessage')"
			@click="removeStep"
			@mouseleave="arming = false"
			@blur="arming = false"
		>
			<Icon name="trash" class="size-5 sm:size-4" />
		</button>
		<RouterLink
			v-if="bubble.role === 'assistant' && bubble.turnId && !bubble.failed"
			:to="`/usage?turno=${bubble.turnId}`"
			@click="closeDock?.()"
			class="ml-1.5 rounded-sm text-3xs text-fg-subtle underline hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
		>
			{{ t("rating.seeTurn") }}
		</RouterLink>
	</div>
</template>
